import React from 'lib/react';
import {
  EVGrid,
  EVRadio,
  EVCheckbox,
  EVChip,
  EVPaper,
  makeStyles,
} from 'lib/components';

import { useTranslation } from 'lib/translation';

import productTooltips from 'features/order/components/Product/productTooltips.json';
import { EVArrowForwardIcon } from '@eagleview/ev-comp-library';
import { useSelector, useDispatch } from 'lib/redux';
import { getTooltipForProduct } from 'features/order/components/Product/ProductList.util';
import ProductTooltip from 'features/order/components/Product/ProductTooltip.component';
import { getEstimatedCost, addUpgradeProductSets } from 'features/finalizeOrder/FinalizeOrder.actions';
import Typography from 'commonComponents/typography';
import { PRODUCT_IDS } from 'constants/AppConstants';
import { ESX_FOR_UPGRADE } from 'constants/FeatureFlag.constants';
import { featureFlagSelector, isSubscriptionDashboardV2EnabledSelector } from 'utils/appSelectors.utils';

const useStyles = makeStyles((theme) => ({
  esxCheckboxContainer: {
    '& .myev-MuiCheckbox-colorPrimary.Mui-checked.Mui-disabled': {
      color: `${theme.evPalette.ocean[200]} !important`,
      opacity: 1,
    },
  },
  addOnsPaper: {
    padding: '12px',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  addOnsContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  addOnsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  esxCheckbox: {
    padding: 0,
  },
  esxLabel: {
    color: '#1B202C',
    fontSize: '16px',
    fontWeight: 400,
    cursor: 'pointer',
  },
  xactimateChip: {
    backgroundColor: '#A6D2FE',
    color: '#172554',
    fontWeight: 500,
    fontSize: '12px',
    height: '24px',
  },
  addOnsDescription: {
    paddingLeft: '32px',
  },
}));

function UpgradeReport() {
  const classes = useStyles();
  const {
    upgradeProductList,
    productId,
    productSets,
    upgradeESX,
    canAddEsxToCurrentProduct,
  } = useSelector(({ placeOrder, finalizeOrder }) => ({
    ...finalizeOrder,
    productSets: placeOrder.productSets,
  }));

  const esxForUpgradeFlag = useSelector(featureFlagSelector(ESX_FOR_UPGRADE));
  const isSubscriptionDashboardV2Enabled = useSelector(isSubscriptionDashboardV2EnabledSelector);
  const isEsxForUpgradeEnabled = Boolean(esxForUpgradeFlag && !isSubscriptionDashboardV2Enabled);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const selectedProduct = productSets?.length > 0 ? productSets[0].primaryProductId : null;
  const selectedAddOnIds = productSets?.[0]?.addOnProductIds ?? [];
  const isEsxSelected = selectedAddOnIds.includes(PRODUCT_IDS.ESX);
  const selectedProductMeta = upgradeProductList?.find(
    (upgradeProduct) => Number(upgradeProduct?.productId) === Number(selectedProduct),
  );

  const selectedProductHasEsxAddon = Array.isArray(selectedProductMeta?.addOns)
    && selectedProductMeta.addOns.includes(PRODUCT_IDS.ESX);
  const isEsxOnlyAddition = Boolean(upgradeESX && canAddEsxToCurrentProduct);
  const showAddOnsSection = Boolean(isEsxForUpgradeEnabled && (isEsxOnlyAddition || selectedProductHasEsxAddon));
  const isEsxReadonly = isEsxOnlyAddition;
  const isEsxChecked = isEsxOnlyAddition ? true : isEsxSelected;

  const handleUpgradeProductChange = (upgradeProductId) => {
    dispatch(addUpgradeProductSets([{
      primaryProductId: upgradeProductId,
      deliveryProductId: 8,
      addOnProductIds: [],
    }]));
    dispatch(getEstimatedCost());
  };

  // #genai: Toggle ESX add-on for the currently selected upgrade primary product
  const handleEsxAddOnToggle = (event) => {
    if (isEsxReadonly) return;
    if (!selectedProduct) return;
    const shouldInclude = event.target.checked;
    const nextAddOns = shouldInclude
      ? Array.from(new Set([...selectedAddOnIds, PRODUCT_IDS.ESX]))
      : selectedAddOnIds.filter((id) => id !== PRODUCT_IDS.ESX);
    dispatch(addUpgradeProductSets([{
      primaryProductId: selectedProduct,
      deliveryProductId: productSets?.[0]?.deliveryProductId ?? 8,
      addOnProductIds: nextAddOns,
    }]));
    dispatch(getEstimatedCost());
  };

  return (
    <EVGrid style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {!isEsxOnlyAddition && (
        <Typography variant="subtitle-400">
          {t('upgrade_report__price_deduction_info', { reportType: productTooltips[productId]?.name })}
        </Typography>
      )}
      <EVGrid
        container
        style={{
          display: 'flex', padding: '10px', backgroundColor: '#E8F5FA', justifyContent: 'space-between',
        }}
      >
        <EVGrid
          xs={isEsxOnlyAddition ? 12 : 5}
          sm={isEsxOnlyAddition ? 12 : 5}
          md={isEsxOnlyAddition ? 12 : 5}
          item
          style={{
            display: 'flex',
            flexDirection: 'column',
            color: '#61687D',
          }}
        >
          <Typography variant="label-600" style={{ textTransform: 'uppercase' }}>{t('finalize_order__current_report')}</Typography>
          <Typography variant="h3-600">
            {productTooltips[productId]?.name}
          </Typography>
        </EVGrid>
        {!isEsxOnlyAddition && (
        <EVGrid
          xs={1}
          sm={1}
          md={1}
          item
        >
          <EVArrowForwardIcon />
        </EVGrid>
        )}
        {!isEsxOnlyAddition && (
        <EVGrid
          item
          xs={5}
          sm={5}
          md={5}
          style={{
            display: 'flex',
            flexDirection: 'column',
            color: '#005A8D',
          }}
        >
          <Typography variant="label-600" style={{ textTransform: 'uppercase' }} data-testid="upgrade-to">
            {t('finalize_order__upgrade_to')}
          </Typography>
          {upgradeProductList?.length === 1 ? (
            <ProductTooltip title={getTooltipForProduct(upgradeProductList[0]?.productId) ?? ''}>
              <Typography
                variant="h3-600"
                style={{ wordWrap: 'anywhere' }}
              >
                {productTooltips[upgradeProductList[0]?.productId]?.name}
              </Typography>
            </ProductTooltip>
          )
            : (
              <EVGrid
                data-testid="upgrade-product-list"
                container
                style={{
                  display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0px',
                }}
              >
                {
          upgradeProductList?.map(({ productId: upgradeProductId }) => (
            // #genai: move stable identity key onto the element returned by the iterator (Sonar S6477); productId is a stable identity, behavior unchanged
            <EVGrid item key={upgradeProductId}>
              {/* #genai The keyed grid directly contains the single tooltip child. */}
              <ProductTooltip title={getTooltipForProduct(upgradeProductId) ?? ''}>
                <EVGrid item style={{ display: 'flex', gap: '6px' }}>
                  <EVRadio
                    checked={selectedProduct === upgradeProductId}
                    onClick={() => handleUpgradeProductChange(upgradeProductId)}
                    color="primary"
                    id={upgradeProductId}
                    value={productTooltips[upgradeProductId]?.name}
                    name="property-type"
                    inputProps={{ 'aria-label': productTooltips[upgradeProductId]?.name }}
                    style={{ padding: 0 }}
                  />
                  <label
                    style={{
                      color: '#1B202C', fontSize: '16px', fontWeight: '400', cursor: 'pointer', wordWrap: 'anywhere',
                    }}
                    htmlFor={upgradeProductId}
                  >
                    {t(productTooltips[upgradeProductId]?.name)}
                  </label>
                </EVGrid>
              </ProductTooltip>
            </EVGrid>
          ))
          }
              </EVGrid>
            )}
        </EVGrid>
        )}
      </EVGrid>
      {/* #genai: Add-ons section for the upgrade flow (ESX file / Xactimate) */}
      {showAddOnsSection && (
        <EVPaper
          variant="outlined"
          data-testid="upgrade-add-ons"
          className={classes.addOnsPaper}
        >
          <Typography variant="label-600" fontColor="charcoal-800">
            {t('finalize_order__add_ons')}
          </Typography>
          <EVGrid className={classes.addOnsContent}>
            <EVGrid
              className={`${classes.esxCheckboxContainer} ${classes.addOnsRow}`}
            >
              <EVCheckbox
                checked={isEsxChecked}
                disabled={isEsxReadonly}
                onChange={handleEsxAddOnToggle}
                color="primary"
                id="upgrade-add-on-esx"
                name="upgrade-add-on-esx"
                data-testid="upgrade-add-on-esx-checkbox"
                inputProps={{ 'aria-label': t('order_history__actions__esx_file') }}
                className={classes.esxCheckbox}
              />
              <label
                htmlFor="upgrade-add-on-esx"
                className={classes.esxLabel}
              >
                {t('order_history__actions__esx_file')}
              </label>
              <EVChip
                size="small"
                label={t('order_history__actions__xactimate')}
                className={classes.xactimateChip}
              />
            </EVGrid>
            <Typography
              variant="subtitle-400"
              fontColor="charcoal-700"
              className={classes.addOnsDescription}
            >
              {t('finalize_order__add_ons_esx_description')}
            </Typography>
          </EVGrid>
        </EVPaper>
      )}
    </EVGrid>
  );
}

export default UpgradeReport;
